import React, { Suspense, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { HomeBaseDrawerWrapper } from 'app/components/CommonStyled/HomeDrawers/HomeBaseDrawer/styled';
import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import { GlobalPersonDrawerCompanyLogo } from 'app/components/Drawers/GlobalPerson/styled';
import { AddBoldIcon } from 'app/components/shared/icons';
import AsteriskIcon from 'app/components/shared/icons/asterisk';
import CheckIcon from 'app/components/shared/icons/check-icon';
import Clock from 'app/components/shared/icons/clock';
import { AnimatePresence, motion } from 'framer-motion';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useUserData from 'hooks/useUserData';
import { ICollectiveDirectoryPersonDrawerResponse } from 'interfaces';
import { foundSelector } from 'store/reducers/nlp/nlp-reducer';
import {
  IPeopleContext,
  PEOPLE_CONTEXT,
  pushSelectedPeople,
  selectedPeopleSelector,
  setSelectedPeople,
  allPreviousFiltersNamesSelector,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';
import { getCompanyLogoUrl, yearsFloatToHumanReadable } from 'utils';

import AddToContactListCTA from '../AddToContactListCTA';
import { IPersonCard } from '../PeopleContent/PeopleList/NewPersonCard';
import useCollectiveIndexPersonDrawer from '../hooks/useCollectiveIndexPersonDrawer';
import PeopleDrawerAccountHolder from './PeopleDrawerAccountHolder';
import CollectiveDirectoryPersonDrawerCTA from './PeopleDrawerCTA';
import { PeopleDrawerContext } from './PeopleDrawerContext';
import PeopleDrawerPulseLead from './PeopleDrawerPulseLead';
import PeopleDrawerRole from './PeopleDrawerRole';
import CollectiveDirectoryPersonDrawerSearchMatches from './PeopleDrawerSearchMatches';
import {
  CollectiveDirectoryPersonDrawerCollapsibleTitle,
  CollectiveDirectoryPersonDrawerCollapsibleTitleContainer,
  CollectiveDirectoryPersonDrawerSeparator,
  CollectiveDirectoryPersonDrawerInformationLabel,
  CollectiveDirectoryPersonDrawerInformationPipe,
  CollectiveDirectoryPersonDrawerCrossKyuTeamingLegend,
  CollectiveDirectoryPersonDrawerCrossKyuTeamingIconBackground,
  CollectiveDirectoryPersonDrawerCrossKyuTeamingText,
  CollectiveDirectoryPersonDrawerInformationViewProfile,
  CollectiveDirectoryPersonDrawerInformationWrapper,
  CollectiveDirectoryPersonDrawerName,
  CollectiveDirectoryPersonDrawerNameContainer,
  CollectiveDirectoryPersonDrawerPronouns,
  CollectiveDirectoryPersonDrawerRole,
  CollectiveDirectoryPersonDrawerRoleSubtext,
  CollectiveDirectoryPersonDrawerSelectButton,
  CollectiveDirectoryPersonDrawerSelectButtonText,
  CollectiveDirectoryPersonDrawerSelectWrapper,
  CollectiveDirectoryPersonDrawerTopContainer,
  CollectiveDirectoryPersonDrawerTopWrapper,
  CollectiveDirectoryPersonDrawerWrapper,
  CollectiveDirectoryPersonDrawerActionsSectionWrapper,
  CollectiveDirectoryPersonDrawerActionsSectionRow,
  CollectiveDirectoryPersonDrawerActionsSectionTitle,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer,
  CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer,
  CollectiveDirectoryPersonDrawerActionsSectionRowInfoText,
  CollectiveDirectoryPersonDrawerSelectButtonCircle,
  CollectiveDirectoryPersonDrawerCollapsibleWrapper,
} from './styled';

const ContactDrawer = React.lazy(() => import('app/components/Drawers/Contact'));

interface CollectiveDirectoryPersonDrawerProps {
  slug: string;
  uuid: string;
  context: IPeopleContext;
  matches?: IPersonCard['matched_detail'];
  children?: React.ReactNode;
  hideAddActions?: boolean;
  fromGlobalDrawer?: boolean;
}

export default function CollectiveDirectoryPersonDrawer({
  slug,
  uuid,
  context,
  matches,
  children,
  hideAddActions = false,
  fromGlobalDrawer = false,
}: CollectiveDirectoryPersonDrawerProps) {
  const { push } = useHistory();
  const userData = useUserData();
  const dispatch = useDispatch();
  const found = useSelector(foundSelector);
  const previousFilters = useSelector(allPreviousFiltersNamesSelector);
  const [showContactDrawer, setShowContactDrawer] = useState<boolean>(false);

  const query = useCollectiveIndexPersonDrawer(uuid || slug, matches, {
    enabled: !!uuid || !!slug,
  });
  const { isLoading, data: person, isError } = query;

  const putTeamingDrawerViewProfileClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.TEAMING_DRAWER_VIEW_PROFILE_CLICKED
  );
  const isTeamable = useMemo(() => {
    return (
      person?.info.cross_kyu_teaming ||
      person?.info.company_slug === userData.kyu_company.slug ||
      context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY
    );
  }, [person, userData, context]);

  const handleViewFullProfileClicked = () => {
    putTeamingDrawerViewProfileClickedBehavioralFunction({
      related_object_uuid: uuid,
    });

    if (context === PEOPLE_CONTEXT.TEAMING) {
      push(`/person/${slug}#professional`);
    } else {
      push(`/person/${slug}`);
    }
  };

  const showWorkExperience = useMemo<boolean>(() => {
    if (!person) return false;

    return (
      (fromGlobalDrawer &&
        person?.matches &&
        Object.values(person?.matches).some((match) => match.length > 0)) ||
      (person?.info?.account_holder_info && person?.info.account_holder_info.length > 0) ||
      person?.info?.pulse_description !== undefined ||
      (person?.work_experience &&
        Object.keys(person?.work_experience).some(
          (key) => person?.work_experience[key] && person?.work_experience[key].length > 0
        ))
    );
  }, [
    fromGlobalDrawer,
    person?.matches,
    person?.info?.account_holder_info,
    person?.info?.pulse_description,
    person?.work_experience,
  ]);

  const isCurrentUser = useMemo<boolean>(() => userData?.uuid === uuid, [userData?.uuid, uuid]);

  if (isLoading || isError || !person) {
    return <CollectiveDirectoryPersonDrawerWrapper />;
  }

  return (
    <ThemeProvider
      theme={{
        context,
        teamable: isTeamable,
        cross_kyu_teaming: person?.info?.cross_kyu_teaming,
        is_alumni: person?.info?.is_alumni,
      }}
    >
      <PeopleDrawerContext.Provider value={{ person, isCurrentUser, setShowContactDrawer }}>
        <CollectiveDirectoryPersonDrawerWrapper>
          <CollectiveDirectoryPersonDrawerTopWrapper>
            <CollectiveDirectoryPersonDrawerTopContainer>
              <PersonAvatar
                size={92}
                avatar={person?.info?.avatar_variants?.lg}
                name={person?.info.name.split(' ')[0]}
                lastName={person?.info.name.split(' ')[0]}
                isAlumni={person?.info?.is_alumni}
                highlighted={!person?.info?.is_alumni}
              />
            </CollectiveDirectoryPersonDrawerTopContainer>
            <CollectiveDirectoryPersonDrawerNameContainer>
              <CollectiveDirectoryPersonDrawerName>
                {person.info.name}
              </CollectiveDirectoryPersonDrawerName>
              {person.info.pronouns ? (
                <CollectiveDirectoryPersonDrawerPronouns>
                  ({person.info.pronouns})
                </CollectiveDirectoryPersonDrawerPronouns>
              ) : null}
            </CollectiveDirectoryPersonDrawerNameContainer>
            <CollectiveDirectoryPersonDrawerRole>
              {!person.info.is_alumni ? (
                <CollectiveDirectoryPersonDrawerRoleSubtext>
                  {person?.info.role_name || 'Works'} at{' '}
                  <GlobalPersonDrawerCompanyLogo
                    src={getCompanyLogoUrl(person?.info.company_slug, true)}
                    alt={`${person?.info.company_name}'s logo`}
                  />
                </CollectiveDirectoryPersonDrawerRoleSubtext>
              ) : (
                <CollectiveDirectoryPersonDrawerRoleSubtext alumni>
                  Alumni at {person?.info.company_name}{' '}
                  <GlobalPersonDrawerCompanyLogo
                    src={getCompanyLogoUrl(person?.info.company_slug, true)}
                    alt={`${person?.info.company_name}'s logo`}
                  />
                </CollectiveDirectoryPersonDrawerRoleSubtext>
              )}
            </CollectiveDirectoryPersonDrawerRole>
            {person.info.is_alumni ? (
              <CollectiveDirectoryPersonDrawerRole style={{ fontSize: '18px' }}>
                <CollectiveDirectoryPersonDrawerRoleSubtext alumni>
                  {person?.info.name.split(' ')[0]} no longer works at {person?.info.company_name}.
                  {person?.info.role_name
                    ? ` They previously had the role ${person?.info.role_name}.`
                    : null}
                </CollectiveDirectoryPersonDrawerRoleSubtext>
              </CollectiveDirectoryPersonDrawerRole>
            ) : null}
            <CollectiveDirectoryPersonDrawerInformationWrapper>
              {(() => {
                const infoParts = [];
                if (person?.info.is_freelancer) {
                  infoParts.push('Freelancer');
                }
                if (person?.info.location) {
                  infoParts.push(person.info.location);
                }
                if (person?.info.role_name) {
                  infoParts.push(person.info.role_name);
                }
                if (person?.info.tenure && person?.info.company_name) {
                  const tenureYears =
                    typeof person.info.tenure === 'number'
                      ? person.info.tenure
                      : parseFloat(person.info.tenure);
                  const tenureText = !isNaN(tenureYears)
                    ? yearsFloatToHumanReadable(tenureYears)
                    : person.info.tenure;
                  infoParts.push(`${tenureText} at ${person.info.company_name}`);
                }
                if (person?.info.work_experience) {
                  const workExperienceYears =
                    typeof person.info.work_experience === 'number'
                      ? person.info.work_experience
                      : parseFloat(person.info.work_experience);
                  const workExperienceText = !isNaN(workExperienceYears)
                    ? yearsFloatToHumanReadable(workExperienceYears)
                    : person.info.work_experience;
                  infoParts.push(`${workExperienceText} of experience`);
                }

                if (infoParts.length > 0) {
                  return (
                    <CollectiveDirectoryPersonDrawerInformationLabel>
                      {infoParts.map((part, index) => (
                        <React.Fragment key={index}>
                          {part}
                          {index < infoParts.length - 1 && (
                            <CollectiveDirectoryPersonDrawerInformationPipe>
                              |
                            </CollectiveDirectoryPersonDrawerInformationPipe>
                          )}
                        </React.Fragment>
                      ))}
                    </CollectiveDirectoryPersonDrawerInformationLabel>
                  );
                }
                return null;
              })()}
              {context === PEOPLE_CONTEXT.TEAMING &&
              person.info.company_name !== userData.company_name ? (
                <CollectiveDirectoryPersonDrawerCrossKyuTeamingLegend
                  isEligible={person.info.cross_kyu_teaming}
                >
                  <CollectiveDirectoryPersonDrawerCrossKyuTeamingIconBackground
                    isEligible={person.info.cross_kyu_teaming}
                  >
                    {person.info.cross_kyu_teaming ? (
                      <CheckIcon
                        width={15}
                        height={15}
                        background="transparent"
                        fill="var(--color-data-industry-background-hover)"
                      />
                    ) : (
                      <Clock width={15} height={15} fill="var(--color-data-passion-foreground)" />
                    )}
                  </CollectiveDirectoryPersonDrawerCrossKyuTeamingIconBackground>
                  <CollectiveDirectoryPersonDrawerCrossKyuTeamingText>
                    {person.info.cross_kyu_teaming
                      ? 'Eligible for cross-company teaming'
                      : 'Not eligible for cross-company teaming'}
                  </CollectiveDirectoryPersonDrawerCrossKyuTeamingText>
                </CollectiveDirectoryPersonDrawerCrossKyuTeamingLegend>
              ) : null}
            </CollectiveDirectoryPersonDrawerInformationWrapper>
          </CollectiveDirectoryPersonDrawerTopWrapper>
          {children ? children : null}
          {(found && Object.keys(found).length > 0) ||
          (previousFilters && Object.keys(previousFilters).length > 0 && !fromGlobalDrawer) ? (
            <CollectiveDirectoryPersonDrawerSearchMatches.SearchConnections
              person={{
                ...person,
                info: { ...person.info, slug: slug, uuid: uuid },
              }}
            />
          ) : null}

          <CollectiveDirectoryPersonDrawerSeparator />
          {showWorkExperience ? (
            <CollectiveDirectoryPersonDrawerCollapsibleWrapper>
              <CollectiveDirectoryPersonDrawerCollapsibleTitleContainer>
                <CollectiveDirectoryPersonDrawerCollapsibleTitle>
                  {person.info.name.split(' ')[0]}'s work experience
                </CollectiveDirectoryPersonDrawerCollapsibleTitle>
              </CollectiveDirectoryPersonDrawerCollapsibleTitleContainer>
              {fromGlobalDrawer &&
              person.matches &&
              Object.values(person.matches).some((match) => match.length > 0) ? (
                <>
                  <CollectiveDirectoryPersonDrawerSearchMatches.Matches person={person} />
                  <CollectiveDirectoryPersonDrawerSeparator />
                </>
              ) : null}

              {person?.info?.account_holder_info && person.info.account_holder_info.length > 0 ? (
                <>
                  <PeopleDrawerAccountHolder />
                  <CollectiveDirectoryPersonDrawerSeparator />
                </>
              ) : null}
              {person?.info?.pulse_description !== undefined ? (
                <>
                  <PeopleDrawerPulseLead />
                  <CollectiveDirectoryPersonDrawerSeparator />
                </>
              ) : null}
              {person.work_experience &&
              Object.keys(person.work_experience).some(
                (key) => person.work_experience[key] && person.work_experience[key].length > 0
              ) ? (
                <CollectiveDirectoryPersonDrawerSearchMatches.WorkExperienceDetails
                  person={{
                    ...person,
                    info: { ...person.info, slug: slug, uuid: uuid },
                  }}
                />
              ) : null}
              {person?.info?.role_description !== undefined &&
              person?.info?.role_description !== null &&
              !person?.info?.is_alumni ? (
                <PeopleDrawerRole />
              ) : null}
            </CollectiveDirectoryPersonDrawerCollapsibleWrapper>
          ) : null}
          {!person.info.is_alumni ? (
            <CollectiveDirectoryPersonDrawerInformationViewProfile
              onClick={handleViewFullProfileClicked}
            >
              View full profile
            </CollectiveDirectoryPersonDrawerInformationViewProfile>
          ) : null}

          <CollectiveDirectoryPersonDrawer.ActionsSection
            person={{ ...person, info: { ...person.info, slug: slug, uuid: uuid } }}
            context={context}
            hideAddActions={hideAddActions}
          />

          {/* COMMON GROUND 
        {connectionCount > 0 ? (
          <PeopleDrawerCollapsible id="common-ground">
            <PeopleDrawerCollapsible.Header>
              <CollectiveDirectoryPersonDrawerCollapsibleTitleContainer>
                <CollectiveDirectoryPersonDrawerCollapsibleTitleIcon>
                  <CommunityIcon
                    width={24}
                    height={24}
                    fill="var(--color-data-community-foreground)"
                  />
                </CollectiveDirectoryPersonDrawerCollapsibleTitleIcon>
                <CollectiveDirectoryPersonDrawerCollapsibleTitle>
                  YOUR CONNECTION TO {person.info.name.split(' ')[0]}
                </CollectiveDirectoryPersonDrawerCollapsibleTitle>
              </CollectiveDirectoryPersonDrawerCollapsibleTitleContainer>
            </PeopleDrawerCollapsible.Header>
            <PeopleDrawerCollapsible.Content>
              <PeopleDrawerCommonGround
                person={{
                  ...person,
                  info: { ...person.info, slug: slug, uuid: uuid },
                }}
                connectionCount={connectionCount}
              />
            </PeopleDrawerCollapsible.Content>
          </PeopleDrawerCollapsible>
        ) : null}

        */}
        </CollectiveDirectoryPersonDrawerWrapper>
        {ReactDOM.createPortal(
          <AnimatePresence>
            {showContactDrawer ? (
              <motion.div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  x: '100%',
                  zIndex: 9999,
                }}
                animate={{
                  x: 0,
                }}
                transition={{
                  duration: 0.45,
                }}
                exit={{
                  x: '100%',
                }}
              >
                <Suspense fallback={<HomeBaseDrawerWrapper />}>
                  <ContactDrawer
                    backToRecipient={() => setShowContactDrawer(false)}
                    selectedUuids={[person.info.uuid]}
                    initialStep={2}
                  />
                </Suspense>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.querySelector('[data-drawer="teaming-contact"]') ||
            document.querySelector('[data-type="sidedrawer"]') ||
            document.getElementById('root')
        )}
      </PeopleDrawerContext.Provider>
    </ThemeProvider>
  );
}

CollectiveDirectoryPersonDrawer.ActionsSectionAddButton =
  function CollectiveDirectoryPersonDrawerActionsSectionAddButton({
    person,
    context,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
    context: IPeopleContext;
    hideAddActions?: boolean;
  }) {
    const selectedPeople = useSelector(selectedPeopleSelector);
    const userData = useUserData();
    const isTeamable = useMemo(() => {
      return (
        person?.info.cross_kyu_teaming ||
        person?.info.company_slug === userData.kyu_company.slug ||
        context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY
      );
    }, [person, userData, context]);
    const dispatch = useDispatch();
    const isPersonSelected = useMemo(() => {
      return selectedPeople.find((selectedPerson) => selectedPerson.uuid === person.info.uuid);
    }, [selectedPeople, person.info.uuid]);
    const teamingDrawerSelectCTAClickedBehavioralFunction = useBehavioral(
      BEHAVIORALS.TEAMING_DRAWER_SELECT_CTA_CLICKED
    );
    const collectiveIndexDrawerSelectCTAClickedBehavioralFunction = useBehavioral(
      BEHAVIORALS.COLLECTIVE_INDEX_DRAWER_SELECT_CTA_CLICKED
    );

    const handleSelectClick = () => {
      if (!isTeamable) return;

      if (isPersonSelected) {
        dispatch(
          setSelectedPeople(
            selectedPeople.filter((selectedPerson) => selectedPerson.uuid !== person.info.uuid)
          )
        );
      } else {
        if (context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) {
          collectiveIndexDrawerSelectCTAClickedBehavioralFunction({
            related_object_uuid: person.info.uuid,
          });
        } else {
          teamingDrawerSelectCTAClickedBehavioralFunction({
            related_object_uuid: person.info.uuid,
          });
        }
        dispatch(
          pushSelectedPeople([{ ...person.info, uuid: person.info.uuid, slug: person.info.slug }])
        );
        window.postMessage('drawers:close');
      }
    };
    return (
      <ThemeProvider
        theme={{
          isPersonSelected,
        }}
      >
        <CollectiveDirectoryPersonDrawerSelectWrapper data-id="people-drawer-select">
          <CollectiveDirectoryPersonDrawerSelectButton
            disabled={!isTeamable}
            onClick={handleSelectClick}
          >
            {isPersonSelected ? (
              <CheckIcon
                fill={'var(--color-primary)'}
                withoutBorder
                width={24}
                height={24}
                background="transparent"
              />
            ) : (
              <CollectiveDirectoryPersonDrawerSelectButtonCircle>
                <AddBoldIcon fill="var(--color-primary)" />
              </CollectiveDirectoryPersonDrawerSelectButtonCircle>
            )}
            <CollectiveDirectoryPersonDrawerSelectButtonText>
              {isPersonSelected ? 'ADDED' : 'ADD'}
            </CollectiveDirectoryPersonDrawerSelectButtonText>
          </CollectiveDirectoryPersonDrawerSelectButton>
        </CollectiveDirectoryPersonDrawerSelectWrapper>
      </ThemeProvider>
    );
  };

CollectiveDirectoryPersonDrawer.ActionsSection =
  function CollectiveDirectoryPersonDrawerActionsSection({
    person,
    context,
    hideAddActions = false,
  }: {
    person: ICollectiveDirectoryPersonDrawerResponse;
    context: IPeopleContext;
    hideAddActions?: boolean;
  }) {
    if (person.info.is_alumni) {
      return <></>;
    }

    const selectedPeople = useSelector(selectedPeopleSelector);
    const isPersonSelected = useMemo(() => {
      return selectedPeople.find((selectedPerson) => selectedPerson.uuid === person.info.uuid);
    }, [selectedPeople, person.info.uuid]);

    const avatarPerson =
      context === PEOPLE_CONTEXT.TEAMING
        ? person.info.is_freelancer
          ? person.info
          : person.info.primary_teaming_leader
        : person.info;

    const contactLegend =
      context === PEOPLE_CONTEXT.TEAMING
        ? person.info.is_freelancer
          ? `Reach out to ${avatarPerson?.name.split(' ')[0]} right away.`
          : person.info.primary_teaming_leader
          ? `Reach out to ${avatarPerson?.name}: ${
              person.info?.name.split(' ')[0]
            }'s point of contact at ${person.info?.company_name}.`
          : `${person.info?.company_name} doesn't have a point of contact assigned at the moment.`
        : `Reach out to ${avatarPerson?.name} right away.`;

    return (
      <>
        <CollectiveDirectoryPersonDrawerSeparator />
        <CollectiveDirectoryPersonDrawerActionsSectionWrapper>
          <CollectiveDirectoryPersonDrawerActionsSectionTitle>
            ACTIONS
          </CollectiveDirectoryPersonDrawerActionsSectionTitle>
          <CollectiveDirectoryPersonDrawerActionsSectionRow>
            <CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer>
              <CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer>
                {avatarPerson ? (
                  <PersonAvatar
                    size={40}
                    avatar={avatarPerson?.avatar_variants?.sm}
                    name={avatarPerson.name.split(' ')[0]}
                    lastName={avatarPerson.name.split(' ')[0]}
                    highlighted={true}
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'rgba(23, 28, 51, 0.30)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AsteriskIcon width={20} height={20} fill="#171C33" />
                  </div>
                )}
              </CollectiveDirectoryPersonDrawerActionsSectionRowInfoIconContainer>
              <CollectiveDirectoryPersonDrawerActionsSectionRowInfoText>
                {contactLegend}
              </CollectiveDirectoryPersonDrawerActionsSectionRowInfoText>
            </CollectiveDirectoryPersonDrawerActionsSectionRowInfoContainer>
            <CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer>
              <CollectiveDirectoryPersonDrawerCTA
                person={{ ...person, info: { ...person.info, uuid: person.info.uuid } }}
                context={context}
                disabled={
                  context === PEOPLE_CONTEXT.TEAMING &&
                  !person.info.is_freelancer &&
                  !person.info.primary_teaming_leader
                }
              />
            </CollectiveDirectoryPersonDrawerActionsSectionRowCTAContainer>
          </CollectiveDirectoryPersonDrawerActionsSectionRow>
          {(person.info.primary_teaming_leader ||
            context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) &&
          !hideAddActions ? (
            <>
              <CollectiveDirectoryPersonDrawerActionsSectionRow>
                <CollectiveDirectoryPersonDrawerActionsSectionTitle>
                  OR
                </CollectiveDirectoryPersonDrawerActionsSectionTitle>
              </CollectiveDirectoryPersonDrawerActionsSectionRow>
              <AddToContactListCTA person={person.info} closeDrawer />
            </>
          ) : null}
        </CollectiveDirectoryPersonDrawerActionsSectionWrapper>
      </>
    );
  };
